/**
 * Lasso selection and transform — pure geometry, no DOM.
 *
 * The engine owns pointer handling and painting; everything that decides *what*
 * is selected and *where it ends up* lives here so it can be tested without a
 * canvas.
 *
 * Two decisions worth stating up front, because they are what make selection
 * feel right rather than merely work:
 *
 * **Selection is by coverage, not by touching.** A stroke counts as selected
 * when most of it is inside the loop. Circling a notehead should not drag along
 * the stem of the note beside it just because the loop clipped its tip.
 *
 * **Snapping quantises the movement, not the ink.** Moving a selection rounds
 * the vertical distance travelled to a whole number of staff steps, so a note
 * sitting cleanly on a line lands cleanly on another line, and a note the
 * learner deliberately wrote high keeps that offset. Rounding absolute position
 * instead would silently "correct" writing the learner has not been told is
 * wrong, which is the opposite of what a teaching app should do.
 */

import type { StaffLayout } from '../theory/staff';
import type { Bounds, InkPoint, Stroke } from './types';
import { strokeBounds, unionBounds } from './types';

export interface Point2D {
  x: number;
  y: number;
}

export interface Transform {
  dx?: number;
  dy?: number;
  /** Uniform scale factor about `origin`. */
  scale?: number;
  origin?: Point2D;
}

/**
 * Ray casting. A point is inside when a ray to +x crosses the polygon an odd
 * number of times. Handles the concave loops people actually draw.
 */
export function pointInPolygon(point: Point2D, polygon: Point2D[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const straddles = a.y > point.y !== b.y > point.y;
    if (!straddles) continue;
    const x = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
    if (point.x < x) inside = !inside;
  }
  return inside;
}

export function polygonBounds(polygon: Point2D[]): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/** Rough polygon area, used to reject a lasso that was really just a tap. */
export function polygonArea(polygon: Point2D[]): number {
  if (polygon.length < 3) return 0;
  let sum = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    sum += (polygon[j].x + polygon[i].x) * (polygon[j].y - polygon[i].y);
  }
  return Math.abs(sum / 2);
}

/** Fraction of a stroke's points lying inside the polygon. */
export function coverage(stroke: Stroke, polygon: Point2D[]): number {
  if (!stroke.points.length) return 0;
  // Cheap rejection first: a stroke whose box misses the loop's box entirely
  // cannot have a single point inside.
  const sb = strokeBounds(stroke);
  const pb = polygonBounds(polygon);
  if (sb.maxX < pb.minX || sb.minX > pb.maxX || sb.maxY < pb.minY || sb.minY > pb.maxY) {
    return 0;
  }
  let hits = 0;
  for (const p of stroke.points) if (pointInPolygon(p, polygon)) hits++;
  return hits / stroke.points.length;
}

export const DEFAULT_COVERAGE = 0.6;

/**
 * Which strokes the loop caught. `minCoverage` is the fraction of a stroke that
 * must fall inside; the default of 0.6 means "most of it", which is forgiving of
 * a loop drawn quickly without being so loose that neighbours come along.
 */
export function strokesInLasso(
  strokes: Stroke[],
  polygon: Point2D[],
  minCoverage: number = DEFAULT_COVERAGE,
): string[] {
  if (polygon.length < 3) return [];
  return strokes.filter((s) => coverage(s, polygon) >= minCoverage).map((s) => s.id);
}

export function selectionBounds(strokes: Stroke[], ids: Iterable<string>): Bounds | null {
  const wanted = new Set(ids);
  const boxes = strokes.filter((s) => wanted.has(s.id)).map(strokeBounds);
  if (!boxes.length) return null;
  return unionBounds(boxes);
}

function transformPoint(p: InkPoint, t: Transform): InkPoint {
  const scale = t.scale ?? 1;
  const origin = t.origin ?? { x: 0, y: 0 };
  const x = scale === 1 ? p.x : origin.x + (p.x - origin.x) * scale;
  const y = scale === 1 ? p.y : origin.y + (p.y - origin.y) * scale;
  return { ...p, x: x + (t.dx ?? 0), y: y + (t.dy ?? 0) };
}

/**
 * Move/scale a stroke. Pressure, tilt and timing are carried through untouched:
 * the learner's hand is preserved, only where it landed changes. Stroke width
 * scales with the ink so an enlarged note does not turn spindly.
 */
export function transformStroke(stroke: Stroke, t: Transform): Stroke {
  const scale = t.scale ?? 1;
  return {
    ...stroke,
    points: stroke.points.map((p) => transformPoint(p, t)),
    width: scale === 1 ? stroke.width : stroke.width * scale,
  };
}

/** Apply a transform to the named strokes, leaving the rest and the order alone. */
export function transformSelection(
  strokes: Stroke[],
  ids: Iterable<string>,
  t: Transform,
): Stroke[] {
  const wanted = new Set(ids);
  if (!wanted.size) return strokes;
  return strokes.map((s) => (wanted.has(s.id) ? transformStroke(s, t) : s));
}

export function deleteSelection(strokes: Stroke[], ids: Iterable<string>): Stroke[] {
  const wanted = new Set(ids);
  return strokes.filter((s) => !wanted.has(s.id));
}

/**
 * Copy the selection, offset so the copy is visibly its own thing, and select
 * the copy rather than the original — the next drag should move what you just
 * made.
 */
export function duplicateSelection(
  strokes: Stroke[],
  ids: Iterable<string>,
  offset: Point2D,
  mintId: () => string,
): { strokes: Stroke[]; ids: string[] } {
  const wanted = new Set(ids);
  const copies: Stroke[] = [];
  for (const s of strokes) {
    if (!wanted.has(s.id)) continue;
    copies.push({ ...transformStroke(s, { dx: offset.x, dy: offset.y }), id: mintId() });
  }
  return { strokes: [...strokes, ...copies], ids: copies.map((c) => c.id) };
}

/** Vertical distance of one diatonic step: half the gap between staff lines. */
export function stepHeight(layout: StaffLayout): number {
  return layout.lineGap / 2;
}

/**
 * Round a vertical drag to a whole number of staff steps.
 *
 * This is what makes dragging a chord up a third land it on lines and spaces
 * instead of between them, while leaving horizontal movement free — nothing
 * about the staff quantises x.
 */
export function snapDeltaY(dy: number, layout: StaffLayout): number {
  const step = stepHeight(layout);
  if (step <= 0) return dy;
  return Math.round(dy / step) * step;
}

/**
 * Vertical pixel delta for moving a selection `steps` diatonic steps *up* the
 * staff. Up the staff is -y on screen, and getting that sign wrong silently
 * inverts every "up a step" control, so it lives here with a test on it rather
 * than inline in a click handler.
 */
export function stepsToDeltaY(steps: number, layout: StaffLayout): number {
  return -steps * stepHeight(layout);
}

/**
 * How far to move so that `anchorY` — a notehead centre — sits exactly on its
 * nearest line or space. Used by the explicit "Snap to staff" action, which is
 * the one place the learner *asks* for their writing to be tidied up.
 */
export function snapToStaffOffset(anchorY: number, layout: StaffLayout): number {
  const step = stepHeight(layout);
  if (step <= 0) return 0;
  const position = Math.round((layout.bottomLineY - anchorY) / step);
  const targetY = layout.bottomLineY - position * step;
  return targetY - anchorY;
}
